import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import ChecklistIcon from "@mui/icons-material/Checklist";
import DoneAllIcon from "@mui/icons-material/DoneAll";
import HearingIcon from "@mui/icons-material/Hearing";
import HearingDisabledIcon from "@mui/icons-material/HearingDisabled";
import PauseIcon from "@mui/icons-material/Pause";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import StickyNote2OutlinedIcon from "@mui/icons-material/StickyNote2Outlined";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VolumeOffIcon from "@mui/icons-material/VolumeOff";
import Box from "@mui/material/Box";
import ButtonBase from "@mui/material/ButtonBase";
import Popover from "@mui/material/Popover";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import React, { useContext, useState } from "react";

import { TestIds } from "@storyboard/web-test-ids/testIds";
import FileFragment from "@/components/FeedItem/FileFragments";
import { formatChipTime } from "@/components/feed/tmsEvent/TmsEventChip";
import TmsEventDetailPopover from "@/components/feed/tmsEvent/TmsEventDetailPopover";
import { tmsEventDetailOf } from "@/components/feed/tmsEvent/tmsEventDetail";
import { tmsEventChipParts, tmsEventChipText } from "@/components/feed/tmsEvent/tmsEventDisplay";
import LinkFragment from "@/components/LinkFragment";
import type {
  HeardReason,
  ListenReceipt,
  MessageActivity,
  PlayedReason,
  ThreadEvent,
  ThreadMessage,
  ThreadTaskCard,
  ThreadTaskEntry,
  ThreadTaskStep,
} from "@/hooks/dispatch/dispatchThread";
import { eventTargetOf } from "@/hooks/dispatch/tasks";
import { AudioAppContext } from "@/models/AudioAppContextProvider";
import { zinc } from "@/ui/tokens";
import { redColor } from "@/utils/color";
import { formatLocalDate } from "@/utils/dateFormat";
import { sanitizeHtmlForDisplay } from "@/utils/sanitizeHtmlForDisplay";
import { DISPATCH_TASK, DISPATCH_TASK_PALETTE } from "../dispatchColors";
import { EVERY_BOARD } from "../taskChip";
import { useTaskCardMenu, useThreadEntryMenu } from "./ThreadTaskMenu";

/**
 * The thread's palette: the driver's bubbles sit back, the dispatcher's lean blue, receipts and audio read green, and
 * a playback the driver may not have heard reads orange.
 */
export const THREAD_COLORS = {
  driverBubble: "#26262c",
  dispatchBubble: "#1c2a45",
  dispatchBorder: "#2f4a7a",
  green: "#4ade80",
  orange: "#fb923c",
  eventPurple: "#c084fc",
} as const;

/**
 * How far the thread's third tier, its events, sits in from the left: one step in from the driver's messages, so an
 * event reads apart from them without drifting toward the middle. A driver's message starts at the thread's 16px
 * gutter, or 16px further in behind its unacknowledged dot; 48px clears both by a visible step.
 */
const EVENT_INDENT = 6;

/** The room above and below each entry in the thread. */
const ENTRY_PY = 1;

/** An entry on the event tier: left-aligned at {@link EVENT_INDENT}, as wide as the rest of the thread allows. */
const EVENT_TIER_SX = { display: "flex", justifyContent: "flex-start", pl: EVENT_INDENT, pr: 2, py: ENTRY_PY } as const;

/** The wash behind a task card: the task orange let into the thread's ground, so the card reads as a task at a glance. */
export const TASK_TINT = `color-mix(in oklch, ${DISPATCH_TASK} 14%, ${zinc[900]})`;

/** The corner radius, in px, of a message bubble, and of the block a task shares with its subject. */
const BUBBLE_RADIUS = 8;

/** What the message details say about a playback that was only played, under its "Played" line. */
export const PLAYED_REASON_TEXT: Record<PlayedReason, string> = {
  deviceMuted: "Device volume was muted when message played",
  driverMaybeAbsent: "Message played but driver may not be present",
};

/** What the message details say about how a heard playback reached the driver, under its "Heard" line. */
export const HEARD_REASON_TEXT: Record<HeardReason, string> = {
  driving: "Message played while driver was driving",
  manualPlay: "Driver manually listened to message",
  repeated: "Driver repeated the message",
};

/** `HH:mm` for a caption. */
export function clockFor(iso: string): string {
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? "" : formatLocalDate(date, "HH:mm");
}

/** `MMM d, yyyy, HH:mm` for a message caption: the classic timeline's date with the Dispatch page's 24-hour clock. */
export function datedClockFor(iso: string): string {
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? "" : formatLocalDate(date, "MMM d, yyyy, HH:mm");
}

/** `m:ss` for a clip length. */
export function clipLength(seconds: number): string {
  const whole = Math.max(0, Math.round(seconds));
  return `${Math.floor(whole / 60)}:${String(whole % 60).padStart(2, "0")}`;
}

/**
 * The "Listen" pill on a message with audio. It drives the app's one audio player: a press on the active message
 * pauses or resumes it, a press on any other starts that one, so the thread never plays two things at once.
 */
function ListenButton({ itemId, seconds }: { itemId: string; seconds: number }) {
  const { activeItemId, playerPlaying, playItem, pauseItem, setActiveItemId } = useContext(AudioAppContext);
  const active = activeItemId === itemId;
  const playing = active && playerPlaying;
  const onClick = () => {
    if (playing) {
      pauseItem();
      return;
    }
    if (active) {
      playItem();
      return;
    }
    setActiveItemId(itemId);
  };
  return (
    <ButtonBase
      data-sb={TestIds.dispatch.threadListen}
      aria-label={playing ? "Pause" : "Listen"}
      aria-pressed={playing}
      onClick={onClick}
      sx={{
        mt: 0.75,
        px: 1,
        height: 22,
        gap: 0.5,
        borderRadius: 11,
        fontSize: "0.6875rem",
        fontWeight: 600,
        color: THREAD_COLORS.green,
        border: `1px solid ${THREAD_COLORS.green}`,
        bgcolor: "rgba(74, 222, 128, 0.08)",
        "&:hover": { bgcolor: "rgba(74, 222, 128, 0.16)" },
      }}
    >
      {playing ? <PauseIcon sx={{ fontSize: 14 }} /> : <PlayArrowIcon sx={{ fontSize: 14 }} />}
      {playing ? "Pause" : "Listen"} {clipLength(seconds)}
    </ButtonBase>
  );
}

const ACTIVITY_LABELS: Record<MessageActivity["kind"], string> = {
  seen: "Seen",
  heard: "Heard",
  played: "Played",
  acknowledged: "Acknowledged",
};

/**
 * The icon for a listen receipt: an ear for heard, a muted speaker or a struck ear for a playback only played. It
 * reads only the kind and a played reason, so a message's receipt and an activity line both fit.
 */
function ListenReceiptIcon({
  receipt,
  size,
}: {
  receipt: { kind: "heard" } | { kind: "played"; reason: PlayedReason };
  size: number;
}) {
  if (receipt.kind === "heard") return <HearingIcon sx={{ fontSize: size, color: THREAD_COLORS.green }} />;
  const sx = { fontSize: size, color: THREAD_COLORS.orange };
  return receipt.reason === "deviceMuted" ? <VolumeOffIcon sx={sx} /> : <HearingDisabledIcon sx={sx} />;
}

function ActivityIcon({ entry }: { entry: MessageActivity }) {
  if (entry.kind === "heard" || entry.kind === "played") return <ListenReceiptIcon receipt={entry} size={14} />;
  const sx = { fontSize: 14, color: THREAD_COLORS.green };
  if (entry.kind === "acknowledged") return <DoneAllIcon sx={sx} />;
  return <VisibilityIcon sx={sx} />;
}

/**
 * The driver's listen receipt in a message's caption: "Heard" in green, or "Played" in orange when the driver may not
 * have heard it, with the reason as the chip's tooltip; the activity list spells the reason out.
 */
function ListenReceiptChip({ receipt }: { receipt: ListenReceipt }) {
  const chip = (
    <Typography
      component="span"
      data-sb={TestIds.dispatch.threadReceipt}
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: 0.25,
        fontSize: "inherit",
        color: receipt.kind === "heard" ? THREAD_COLORS.green : THREAD_COLORS.orange,
      }}
    >
      <ListenReceiptIcon receipt={receipt} size={13} /> {ACTIVITY_LABELS[receipt.kind]}
    </Typography>
  );
  if (receipt.kind === "heard") return chip;
  return <Tooltip title={PLAYED_REASON_TEXT[receipt.reason]}>{chip}</Tooltip>;
}

/**
 * The activity button in a message's caption and the list it opens: every receipt on the message, oldest first, each
 * saying what happened, who did it, and when; the driver's listen receipt says under its line how the playback reached
 * them, or why they may not have heard it. Each is dated as the caption is, since a receipt can be days younger than
 * its message. Acknowledgements of one message live here rather than in the thread's event log, which is for the
 * channel.
 */
function ActivityButton({ activity }: { activity: readonly MessageActivity[] }) {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  return (
    <>
      <ButtonBase
        data-sb={TestIds.dispatch.threadActivity}
        aria-label="Message activity"
        aria-expanded={anchor != null}
        onClick={(event) => setAnchor(event.currentTarget)}
        sx={{
          px: 0.5,
          height: 16,
          borderRadius: 0.5,
          fontSize: "inherit",
          color: zinc[500],
          "&:hover": { color: zinc[200], bgcolor: zinc[800] },
        }}
      >
        {activity.length === 0 ? "activity" : `${activity.length} ${activity.length === 1 ? "receipt" : "receipts"}`}
      </ButtonBase>
      <Popover
        open={anchor != null}
        anchorEl={anchor}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        slotProps={{
          paper: {
            "data-sb": TestIds.dispatch.threadActivityList,
            sx: { bgcolor: zinc[800], color: "white", border: `1px solid ${zinc[600]}`, p: 1, minWidth: 220 },
          } as React.ComponentProps<typeof Popover>["PaperProps"],
        }}
      >
        {activity.length === 0 ? (
          <Typography sx={{ px: 0.5, py: 0.25, fontSize: "0.75rem", color: zinc[400] }}>No activity yet</Typography>
        ) : (
          activity.map((entry) => (
            <Box
              key={`${entry.kind}:${entry.actorName ?? ""}:${entry.at}`}
              sx={{ px: 0.5, py: 0.375, fontSize: "0.75rem" }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                <ActivityIcon entry={entry} />
                <Box component="span" sx={{ color: zinc[100] }}>
                  {ACTIVITY_LABELS[entry.kind]}
                  {entry.actorName ? ` · ${entry.actorName}` : ""}
                </Box>
                <Box
                  component="span"
                  sx={{
                    ml: "auto",
                    pl: 1.5,
                    color: zinc[500],
                    fontFamily: "ui-monospace, Menlo, monospace",
                    whiteSpace: "nowrap",
                  }}
                >
                  {datedClockFor(entry.at)}
                </Box>
              </Box>
              {entry.kind === "played" && (
                <Typography sx={{ pl: 2.75, fontSize: "0.6875rem", color: THREAD_COLORS.orange }}>
                  {PLAYED_REASON_TEXT[entry.reason]}
                </Typography>
              )}
              {entry.kind === "heard" && entry.reason && (
                <Typography sx={{ pl: 2.75, fontSize: "0.6875rem", color: zinc[400] }}>
                  {HEARD_REASON_TEXT[entry.reason]}
                </Typography>
              )}
            </Box>
          ))
        )}
      </Popover>
    </>
  );
}

/**
 * One message in the thread, on one of its three tiers: the driver's on the left in grey, anyone else's on the right in
 * blue, each with its text, its files and links, a Listen pill when there is audio, and a caption saying who and when,
 * with the activity button that opens who saw, heard, and acknowledged it. A dispatcher's message carries the driver's
 * receipts in the caption: Seen, and Heard or Played; a driver's message carries the red dot while no dispatcher has
 * handled it. A message with nothing to show yet says whether its content is still on its way from the server or still
 * being transcribed. A message a task is about carries the task's card under its bubble, in one orange frame with it; a
 * right-click on the bubble offers to acknowledge a driver's message, or take that back, then to make a task on the
 * message or its driver, and to move the one it has.
 */
export const ThreadMessageBubble = React.memo(function ThreadMessageBubble({ message }: { message: ThreadMessage }) {
  const mine = !message.fromDriver;
  // A dispatcher's own message waits on no one, so only the driver's offers to be acknowledged.
  const entryMenu = useThreadEntryMenu(
    { subject: "feed_item", targetId: message.id },
    message.fromDriver ? message.acknowledged : null,
  );
  const who = message.fromDriver ? "Driver" : (message.senderName ?? "Dispatch");
  const html = message.text ? sanitizeHtmlForDisplay(message.text) : "";
  const withTasks = message.tasks.length > 0;
  let border: string = mine ? THREAD_COLORS.dispatchBorder : zinc[700];
  if (withTasks) border = DISPATCH_TASK;
  return (
    <Box
      data-sb={TestIds.dispatch.threadMessage}
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: mine ? "flex-end" : "flex-start",
        px: 2,
        py: ENTRY_PY,
      }}
    >
      <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1, maxWidth: "78%" }}>
        {!message.fromDriver || message.acknowledged ? null : (
          <Box
            aria-label="Unacknowledged"
            sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: redColor, mt: 1.75, flex: "none" }}
          />
        )}
        {/* The bubble and its task cards stretch to one width, so the frame they share reads as one block. */}
        <Box onContextMenu={entryMenu.onContextMenu} sx={{ display: "flex", flexDirection: "column", minWidth: 0 }}>
          <Box
            sx={{
              px: 1.5,
              py: 1.25,
              borderRadius: withTasks ? `${BUBBLE_RADIUS}px ${BUBBLE_RADIUS}px 0 0` : `${BUBBLE_RADIUS}px`,
              bgcolor: mine ? THREAD_COLORS.dispatchBubble : THREAD_COLORS.driverBubble,
              border: `1px solid ${border}`,
              color: zinc[100],
              fontSize: "0.875rem",
              lineHeight: 1.45,
              minWidth: 0,
            }}
          >
            {html && <Box className="display-transcript" dangerouslySetInnerHTML={{ __html: html }} />}
            {message.files.map((file) => (
              <Box key={file.id} sx={{ mt: html ? 1 : 0 }}>
                {FileFragment(file)}
              </Box>
            ))}
            {message.links.map((link) => (
              <Box key={link.id} sx={{ mt: 1 }}>
                {LinkFragment(link)}
              </Box>
            ))}
            {!html && message.files.length === 0 && message.links.length === 0 && (
              <Typography sx={{ fontSize: "inherit", color: zinc[500], fontStyle: "italic" }}>
                {message.audio ? "Voice message" : message.contentLoaded ? "Processing…" : "Loading…"}
              </Typography>
            )}
            {message.audio && <ListenButton itemId={message.id} seconds={message.audio.seconds} />}
          </Box>
          {withTasks && <AttachedTasks tasks={message.tasks} />}
        </Box>
      </Box>
      {entryMenu.menu}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 0.75,
          mt: 0.5,
          pl: message.fromDriver && !message.acknowledged ? 2 : 0,
          fontSize: "0.6875rem",
          color: zinc[500],
        }}
      >
        <Typography component="span" sx={{ fontSize: "inherit" }}>
          {who} · {datedClockFor(message.at)}
        </Typography>
        {mine && message.seen && (
          <Typography
            component="span"
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: 0.25,
              fontSize: "inherit",
              color: THREAD_COLORS.green,
            }}
          >
            <DoneAllIcon sx={{ fontSize: 13 }} /> Seen
          </Typography>
        )}
        {mine && message.listened && <ListenReceiptChip receipt={message.listened} />}
        <ActivityButton activity={message.activity} />
      </Box>
    </Box>
  );
});

/**
 * A TMS event between the messages, on the thread's event tier, as the catalog's chip: the same text the driver feed's
 * chip shows, so a dispatcher reads the same event the same way on both surfaces, and opens the same detail on click
 * when it has any. Its emoji sits in a column of its own, so text that wraps lines up under the first line's. WEB-2277
 * colors it by category. An event a task is about is the top row of an orange block, its task's card the row beneath;
 * the chip keeps its text and its click. A right-click offers to acknowledge the event, or take that back, then to make
 * a task on the event, when a task can name its subject, or on its driver, and to move the one it has.
 */
function TmsEventEntry({ event }: { event: Extract<ThreadEvent, { event: "tmsEvent" }> }) {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const entryMenu = useThreadEntryMenu(eventTargetOf(event.tmsEvent), event.acknowledged);
  const text = tmsEventChipText(event.tmsEvent, formatChipTime);
  const parts = tmsEventChipParts(event.tmsEvent, formatChipTime);
  const hasDetail = tmsEventDetailOf(event.tmsEvent) !== null;
  const withTasks = event.tasks.length > 0;
  const chip = (
    <Tooltip title={text}>
      <Box
        role={hasDetail ? "button" : undefined}
        tabIndex={hasDetail ? 0 : undefined}
        onContextMenu={entryMenu.onContextMenu}
        onClick={
          hasDetail
            ? (click: React.MouseEvent<HTMLElement>) => {
                click.stopPropagation();
                setAnchorEl(click.currentTarget);
              }
            : undefined
        }
        onKeyDown={
          hasDetail
            ? (key: React.KeyboardEvent<HTMLElement>) => {
                if (key.key !== "Enter" && key.key !== " ") return;
                key.preventDefault();
                key.stopPropagation();
                setAnchorEl(key.currentTarget);
              }
            : undefined
        }
        sx={{
          cursor: hasDetail ? "pointer" : undefined,
          display: "inline-flex",
          alignItems: "flex-start",
          gap: 0.75,
          px: 1.25,
          py: 0.5,
          borderRadius: withTasks ? `${BUBBLE_RADIUS}px ${BUBBLE_RADIUS}px 0 0` : 13,
          border: `1px solid ${withTasks ? DISPATCH_TASK : zinc[700]}`,
          bgcolor: zinc[800],
          fontSize: "0.6875rem",
          lineHeight: 1.5,
          color: zinc[300],
          maxWidth: "100%",
        }}
      >
        <Box component="span" aria-hidden sx={{ flex: "none" }}>
          {parts.emoji}
        </Box>
        <Box component="span" sx={{ minWidth: 0 }}>
          {parts.text}
        </Box>
      </Box>
    </Tooltip>
  );
  return (
    <Box data-sb={TestIds.dispatch.threadEvent} sx={EVENT_TIER_SX}>
      {withTasks ? (
        <Box
          onContextMenu={entryMenu.onContextMenu}
          sx={{ display: "flex", flexDirection: "column", maxWidth: "100%", minWidth: 0 }}
        >
          {chip}
          <AttachedTasks tasks={event.tasks} />
        </Box>
      ) : (
        chip
      )}
      {entryMenu.menu}
      <TmsEventDetailPopover event={event.tmsEvent} anchorEl={anchorEl} onClose={() => setAnchorEl(null)} />
    </Box>
  );
}

/** The divider before a day's first entry in the thread: its date between two rules, as the classic timeline has. */
export function ThreadDateDivider({ label }: { label: string }) {
  return (
    <Box
      data-sb={TestIds.dispatch.threadDate}
      role="separator"
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1.5,
        px: 2,
        py: 1,
        fontSize: "0.6875rem",
        fontWeight: 600,
        color: zinc[500],
        "&::before, &::after": { content: '""', flex: 1, borderTop: `1px solid ${zinc[800]}` },
      }}
    >
      {label}
    </Box>
  );
}

/**
 * One event in the thread, drawn as a pill on the event tier between the messages.
 *
 * A channel acknowledgement naming who handled the channel and when, or a TMS event from the driver's day. Its time
 * carries no date: the day's divider dates it.
 */
export const ThreadEventPill = React.memo(function ThreadEventPill({ event }: { event: ThreadEvent }) {
  switch (event.event) {
    case "tmsEvent":
      return <TmsEventEntry event={event} />;
    case "acknowledged":
      return <ChannelAcknowledgedPill event={event} />;
  }
});

/**
 * The pill the channel acknowledgement is drawn as, on the event tier: an icon in a column of its own, then a label in
 * the event's accent, its clock time, and who did it, which wrap under one another rather than under the icon.
 */
function EventPill({
  icon,
  accent,
  border,
  bgcolor,
  label,
  title,
  at,
  actorName,
}: {
  icon: React.ReactElement;
  /** The color of the label. */
  accent: string;
  border: string;
  bgcolor?: string;
  label: string;
  /** What the tooltip says. */
  title: string;
  at: string;
  actorName: string | null;
}) {
  return (
    <Box data-sb={TestIds.dispatch.threadEvent} sx={EVENT_TIER_SX}>
      <Tooltip title={title}>
        <Box
          sx={{
            display: "inline-flex",
            alignItems: "flex-start",
            gap: 0.75,
            px: 1.25,
            py: 0.5,
            maxWidth: "100%",
            borderRadius: 13,
            border: `1px solid ${border}`,
            bgcolor,
            fontSize: "0.6875rem",
            lineHeight: 1.5,
            color: zinc[300],
          }}
        >
          <Box component="span" sx={{ flex: "none", display: "flex", height: "1.5em", alignItems: "center" }}>
            {icon}
          </Box>
          <Box component="span" sx={{ minWidth: 0, display: "flex", flexWrap: "wrap", columnGap: 0.75 }}>
            <Box component="span" sx={{ color: accent, fontWeight: 600, whiteSpace: "nowrap" }}>
              {label}
            </Box>
            <Box component="span" sx={{ fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace" }}>
              {clockFor(at)}
            </Box>
            {actorName && <Box component="span">· {actorName}</Box>}
          </Box>
        </Box>
      </Tooltip>
    </Box>
  );
}

/** A channel acknowledgement's entry: who handled the channel, and when. */
function ChannelAcknowledgedPill({ event }: { event: Extract<ThreadEvent, { event: "acknowledged" }> }) {
  const label = "Acknowledged channel";
  return (
    <EventPill
      icon={<DoneAllIcon sx={{ fontSize: 14, color: THREAD_COLORS.green }} />}
      accent={THREAD_COLORS.green}
      border={THREAD_COLORS.dispatchBorder}
      bgcolor="rgba(47, 74, 122, 0.25)"
      label={label}
      title={event.actorName ? `${label} by ${event.actorName}` : label}
      at={event.at}
      actorName={event.actorName}
    />
  );
}

/** What a standalone task card is headed by: what the task is about, since no entry above it says. */
const TASK_ABOUT_LABELS: Record<ThreadTaskCard["about"], string> = {
  driver: "Driver task",
  message: "Task · earlier message",
  event: "Task · earlier event",
};

/** The note line a task card shows: the assigner's name ahead of the note, or the note alone when there is no name. */
export function taskCardNoteLine(card: Pick<ThreadTaskCard, "note" | "assignerName">): string | null {
  const note = card.note?.trim();
  if (!note) return null;
  return card.assignerName ? `${card.assignerName}: ${note}` : note;
}

/** What a step line says: the board a task moved to, or that it is done. */
export function taskStepLabel(step: ThreadTaskStep): string {
  if (step.kind === "done") return "Done";
  return step.viewLabel ? `Moved to ${step.viewLabel}` : "Moved";
}

/** A time in the task card's monospace, dated: a task's steps can be days apart from the entry it is drawn under. */
function TaskClock({ at }: { at: string }) {
  return (
    <Box
      component="span"
      sx={{ ml: "auto", pl: 1, color: zinc[500], fontFamily: "ui-monospace, Menlo, monospace", whiteSpace: "nowrap" }}
    >
      {datedClockFor(at)}
    </Box>
  );
}

/** The badge saying where a task stands: "Open" filled in the task orange, "Done" outlined in the receipt green. */
function TaskStateBadge({ state }: { state: ThreadTaskCard["state"] }) {
  const open = state === "open";
  return (
    <Box
      component="span"
      sx={{
        flex: "none",
        px: 0.75,
        height: 16,
        lineHeight: "16px",
        borderRadius: 8,
        fontSize: "0.625rem",
        fontWeight: 700,
        letterSpacing: "0.02em",
        textTransform: "uppercase",
        ...(open
          ? { bgcolor: DISPATCH_TASK, color: zinc[950] }
          : { border: `1px solid ${THREAD_COLORS.green}`, color: THREAD_COLORS.green }),
      }}
    >
      {open ? "Open" : "Done"}
    </Box>
  );
}

/**
 * The lines every task card shares: the heading with the task's board (every card names one), its assigner, when it was
 * made, and its state; the note as `[assigner]: [note]` behind the note icon; and one line per later step, dated, since
 * a move or a handling can be days after the task was made. A right-click on an open task's card offers to acknowledge
 * or move it, on a done one to reopen it, and never reaches the message or event the card is drawn under.
 */
function TaskCardBody({ card, heading }: { card: ThreadTaskCard; heading: string }) {
  const noteLine = taskCardNoteLine(card);
  const entryMenu = useTaskCardMenu(card);
  return (
    <Box
      data-sb={TestIds.dispatch.threadTask}
      onContextMenu={entryMenu.onContextMenu}
      sx={{ display: "flex", flexDirection: "column", gap: 0.375, px: 1.25, py: 0.75, fontSize: "0.6875rem" }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, minWidth: 0, color: zinc[300] }}>
        <ChecklistIcon aria-hidden sx={{ flex: "none", fontSize: 14, color: DISPATCH_TASK }} />
        <Box component="span" sx={{ color: DISPATCH_TASK_PALETTE.text, fontWeight: 600, whiteSpace: "nowrap" }}>
          {heading}
        </Box>
        <Box
          component="span"
          data-sb={TestIds.dispatch.threadTaskBoard}
          sx={{
            flex: "none",
            px: 0.75,
            height: 18,
            lineHeight: "18px",
            borderRadius: 9,
            border: `1px solid ${DISPATCH_TASK}`,
            color: DISPATCH_TASK,
            fontWeight: 600,
            whiteSpace: "nowrap",
          }}
        >
          {card.viewLabel ?? EVERY_BOARD}
        </Box>
        {card.assignerName && (
          <Box component="span" sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {card.assignerName}
          </Box>
        )}
        <TaskClock at={card.at} />
        <TaskStateBadge state={card.state} />
      </Box>
      {noteLine && (
        <Box sx={{ display: "flex", alignItems: "flex-start", gap: 0.5, color: zinc[200], fontSize: "0.75rem" }}>
          <StickyNote2OutlinedIcon aria-label="Task note" sx={{ flex: "none", fontSize: 14, color: DISPATCH_TASK }} />
          <Box component="span" sx={{ minWidth: 0, overflowWrap: "anywhere" }}>
            {noteLine}
          </Box>
        </Box>
      )}
      {card.steps.map((step) => (
        <Box
          key={`${step.kind}:${step.at}`}
          sx={{ display: "flex", alignItems: "center", gap: 0.5, pl: 0.25, color: zinc[400], minWidth: 0 }}
        >
          {step.kind === "done" ? (
            <DoneAllIcon sx={{ flex: "none", fontSize: 13, color: THREAD_COLORS.green }} />
          ) : (
            <ArrowForwardIcon sx={{ flex: "none", fontSize: 13, color: DISPATCH_TASK }} />
          )}
          <Box
            component="span"
            sx={{ whiteSpace: "nowrap", color: step.kind === "done" ? THREAD_COLORS.green : zinc[300] }}
          >
            {taskStepLabel(step)}
          </Box>
          {step.actorName && (
            <Box component="span" sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              · {step.actorName}
            </Box>
          )}
          <TaskClock at={step.at} />
        </Box>
      ))}
      {entryMenu.menu}
    </Box>
  );
}

/**
 * The task cards under a message bubble or an event chip, continuing the orange frame the subject opened: the frame
 * closes under the last card, and a rule parts one card from the next.
 */
function AttachedTasks({ tasks }: { tasks: readonly ThreadTaskCard[] }) {
  return (
    <Box
      sx={{
        border: `1px solid ${DISPATCH_TASK}`,
        borderTop: 0,
        borderRadius: `0 0 ${BUBBLE_RADIUS}px ${BUBBLE_RADIUS}px`,
        bgcolor: TASK_TINT,
        minWidth: 0,
        "& > * + *": { borderTop: `1px solid color-mix(in oklch, ${DISPATCH_TASK} 40%, transparent)` },
      }}
    >
      {tasks.map((card) => (
        <TaskCardBody key={card.id} card={card} heading="Task" />
      ))}
    </Box>
  );
}

/**
 * A task on its own between the messages: one about the driver, or about a message or event the thread does not hold.
 * It sits on the event tier, in line with the channel acknowledgements, and is framed in the task orange with a bar down
 * its left edge, so it never reads as a message.
 */
export const ThreadTaskBlock = React.memo(function ThreadTaskBlock({ entry }: { entry: ThreadTaskEntry }) {
  return (
    <Box sx={EVENT_TIER_SX}>
      <Box
        sx={{
          width: "78%",
          minWidth: 0,
          border: `1px solid ${DISPATCH_TASK}`,
          borderLeft: `3px solid ${DISPATCH_TASK}`,
          borderRadius: `${BUBBLE_RADIUS}px`,
          bgcolor: TASK_TINT,
        }}
      >
        <TaskCardBody card={entry.task} heading={TASK_ABOUT_LABELS[entry.task.about]} />
      </Box>
    </Box>
  );
});
